import { cn } from "./cn";

/**
 * Grid3DBackdrop — WO#5 Neon-Editorial v2.
 *
 * Fixed atmospheric backdrop, deliberately quiet. Composes:
 *   1. A very faint teal top-halo (30° cast, hero-anchored)
 *   2. A coral horizon anchor at the bottom edge
 *   3. Sparse dot-field particles (SVG mask) that catch the eye
 *      without competing with the isometric world artwork
 *   4. Fine noise grain
 *
 * No perspective floor, no city silhouette — the ONE isometric
 * world lives inside the Hero as the actual product illustration
 * (see IsoWorld). This backdrop is atmosphere, not scene.
 */
export function Grid3DBackdrop({
  variant = "narrative",
}: {
  variant?: "narrative" | "fullscreen";
}) {
  const pos = variant === "narrative" ? "fixed" : "absolute";
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none inset-0 overflow-hidden select-none",
        pos === "fixed" ? "fixed" : "absolute",
      )}
      style={{ zIndex: 0 }}
    >
      {/* Sparse dot-field particles */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(DOTS_SVG)}")`,
          backgroundRepeat: "repeat",
          backgroundSize: "560px 560px",
          opacity: 0.55,
        }}
      />
      {/* Coral horizon anchor */}
      <div
        className="absolute inset-x-0 bottom-0"
        style={{
          height: "26vh",
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(255,71,87,0.13) 0%, transparent 65%)",
        }}
      />
      {/* Teal top-halo (hero-anchored) */}
      <div
        className="absolute inset-x-0 top-0"
        style={{
          height: "38vh",
          background:
            "radial-gradient(ellipse at 78% 0%, rgba(45, 182, 240,0.10) 0%, transparent 55%)",
        }}
      />
      {/* Grain */}
      <div className="grain-overlay" />
    </div>
  );
}

const DOTS_SVG = `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 560 560'>
  <g fill='#2DB6F0' opacity='0.55'>
    <circle cx='42'  cy='88'  r='0.8'/>
    <circle cx='190' cy='140' r='0.6'/>
    <circle cx='320' cy='58'  r='0.9'/>
    <circle cx='488' cy='118' r='0.6'/>
    <circle cx='102' cy='236' r='0.7'/>
    <circle cx='260' cy='302' r='0.9'/>
    <circle cx='410' cy='260' r='0.6'/>
    <circle cx='150' cy='380' r='0.8'/>
    <circle cx='340' cy='420' r='0.6'/>
    <circle cx='500' cy='340' r='0.9'/>
    <circle cx='72'  cy='488' r='0.7'/>
    <circle cx='230' cy='510' r='0.6'/>
    <circle cx='396' cy='476' r='0.9'/>
  </g>
  <g fill='#FF4757' opacity='0.35'>
    <circle cx='120' cy='42'  r='0.8'/>
    <circle cx='380' cy='192' r='0.6'/>
    <circle cx='60'  cy='320' r='0.7'/>
    <circle cx='450' cy='400' r='0.9'/>
    <circle cx='300' cy='520' r='0.6'/>
  </g>
</svg>`;
